import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { EASE } from "@/lib/motion";
import { SERVICE_LIST } from "@/content/services";

const NAV = [
  { to: "/brands", label: "Brands & Range" },
  { label: "Services", children: SERVICE_LIST.map((s) => ({ to: s.path, label: s.navLabel })) },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Logo = ({ light = false, className = "" }) => (
  <Link to="/" data-testid="nav-logo" aria-label="Deliver home" className={`inline-flex items-center ${className}`}>
    <img
      src="/deliver-logo.png"
      alt="Deliver"
      className={`h-8 sm:h-9 w-auto object-contain transition-[filter] duration-300 ${light ? "brightness-0 invert" : ""}`}
    />
  </Link>
);

const linkCls = (isActive) => `link-underline text-[0.8rem] tracking-[0.14em] uppercase font-semibold transition-colors ${isActive ? "text-[var(--terracotta)]" : "text-[var(--ink)] opacity-80 hover:opacity-100 hover:text-[var(--terracotta)]"}`;

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setSvcOpen(false); }, [pathname]);

  const svcActive = SERVICE_LIST.some((s) => s.path === pathname);

  return (
    <>
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${scrolled || svcOpen ? "bg-[rgba(255,252,242,0.96)] backdrop-blur-md shadow-[0_1px_0_0_var(--sand)]" : "bg-transparent"}`}
        data-testid="site-navbar"
      >
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
          <Logo light={open && !scrolled} />
          <nav className="hidden lg:flex items-center gap-10" aria-label="Main">
            {NAV.map((n) => n.children ? (
              <div key={n.label} className="relative" onMouseEnter={() => setSvcOpen(true)} onMouseLeave={() => setSvcOpen(false)}>
                <button className={`${linkCls(svcActive)} inline-flex items-center gap-1`} onClick={() => setSvcOpen(!svcOpen)} aria-expanded={svcOpen} data-testid="nav-services-toggle">{n.label} <ChevronDown size={12} className={`transition-transform ${svcOpen ? "rotate-180" : ""}`} /></button>
                <AnimatePresence>
                  {svcOpen && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.25, ease: EASE }} className="absolute left-1/2 -translate-x-1/2 top-full pt-4" data-testid="nav-services-menu">
                      <div className="bg-white border border-[var(--sand)] rounded-sm shadow-xl p-2 min-w-[260px]">
                        {n.children.map((c) => (
                          <NavLink key={c.to} to={c.to} data-testid={`nav-link-${c.to.replace("/", "")}`} className={({ isActive }) => `block px-4 py-2.5 text-sm rounded-sm transition-colors ${isActive ? "text-[var(--terracotta)]" : "text-[var(--ink)] hover:bg-[var(--cream-2)]"}`}>{c.label}</NavLink>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink key={n.to} to={n.to} data-testid={`nav-link-${n.to.replace("/", "")}`} className={({ isActive }) => linkCls(isActive)}>{n.label}</NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/become-a-stockist" data-testid="nav-cta-stockist" className="hidden sm:inline-flex btn-pill btn-terracotta !py-3 !px-5">
              Become a stockist <ArrowUpRight size={14} />
            </Link>
            <button data-testid="nav-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden p-2 text-[var(--ink)]" aria-label="Menu">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="mobile-menu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="fixed inset-0 z-40 bg-[var(--ink)] text-[var(--cream)] flex flex-col pt-24 px-8 pb-10 overflow-y-auto">
            <button onClick={() => setOpen(false)} className="absolute top-6 right-6 p-2" aria-label="Close menu" data-testid="mobile-menu-close"><X size={22} /></button>
            <nav className="flex flex-col gap-1">
              {[{ to: "/", label: "Home" }, { to: "/brands", label: "Brands & Range" }, { to: "/about", label: "About" }, { to: "/contact", label: "Contact" }, { to: "/become-a-stockist", label: "Become a stockist" }].map((n, i) => (
                <motion.div key={n.to} initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 + i * 0.05, duration: 0.7, ease: EASE }}>
                  <Link to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="font-display text-3xl sm:text-4xl font-light block py-1.5 hover:text-[var(--terracotta)] transition-colors">{n.label}</Link>
                </motion.div>
              ))}
            </nav>
            <p className="eyebrow text-[var(--stone)] mt-8 mb-3">Services</p>
            <nav className="flex flex-col gap-2">
              {SERVICE_LIST.map((s) => <Link key={s.slug} to={s.path} data-testid={`mobile-nav-${s.slug}`} className="text-lg font-light text-[var(--cream)]/85 hover:text-[var(--terracotta)]">{s.navLabel}</Link>)}
            </nav>
            <div className="mt-auto pt-10 text-sm text-[var(--stone)] space-y-1">
              <p>39 Sunblest Crescent, Mount Druitt NSW 2770</p>
              <p>(02) 9799 8483 · info@deliverfood.com.au</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
